import { landmineNames } from '@mm/constants/landmine-names';
import SuggestionModel from '@mm/state/model/SuggestionModel';
import { useModalActions } from '@mm/state/modules/modalsState/actions';
import { combinedFeatureFlagsSelector } from '@mm/state/selectors/featureFlagsSelectors';
import { activeChatbotIdSelector } from '@mm/state/selectors/viewSelectors';
import classnames from 'classnames';
import React from 'react';
import { useSelector } from 'react-redux';
import useRouter from 'use-react-router';
import Card from '../../../../../../common/components/MainCard/MainCard';
import { TooltipGuard } from '../../../../../../modules/permissions/RequiresPermissions';
import { BotBuilderRoutes } from '../../../../../../routes/bot-builder-routes';
import { TriggerSidePaneQuestionItem } from './TriggerSidePaneQuestionItem/TriggerSidePaneQuestionItem';
import './TriggerSidePaneQuestions.scss';

type Props = {
  questions: SuggestionModel[];
  onIgnore: (id: number) => void;
  loading: boolean;
};

export const TriggerSidePaneQuestions = (props: Props) => {
  const { onIgnore = () => {}, questions, loading } = props;
  const { history } = useRouter();
  const chatbotId = useSelector(activeChatbotIdSelector);

  const canViewUnansweredQuestions = useSelector(
    combinedFeatureFlagsSelector,
  ).analyticsBotViewUnansweredQuestions;

  const { openUniversalUpgradeLandmineModal } = useModalActions();

  const onQuestionIgnore = (missedId: number) => () => {
    onIgnore(missedId);
  };

  const renderItems = () => {
    const questionsPreview = questions.slice(0, 10);
    return questionsPreview.map(
      ({ missedText, occurrences, id }, index, arr) => (
        <div
          key={id}
          className={classnames('trigger-side-pane-questions__item', {
            'trigger-side-pane-questions__item--bordered':
              index < arr.length - 1,
          })}
        >
          <TriggerSidePaneQuestionItem
            questionText={missedText}
            count={String(occurrences)}
            onIgnore={onQuestionIgnore(id)}
          />
        </div>
      ),
    );
  };

  const hasQuestions = () => questions.length !== 0;
  const navigateToBotAnalytics = () =>
    history.push(BotBuilderRoutes.botAnalytics.getLink({ chatbotId }));
  const onViewMore = () => {
    if (!canViewUnansweredQuestions) {
      openUniversalUpgradeLandmineModal({
        landmineName: `${landmineNames.QA_UNANSWERED_QUESTIONS}`,
        feature: 'analyticsBotViewUnansweredQuestions',
        onSuccessBilling: navigateToBotAnalytics,
      });
    } else {
      navigateToBotAnalytics();
    }
  };

  const renderedQuestions = (() => {
    if (loading) {
      return (
        <>
          <i className="fas fa-spinner fa-spin" /> Loading
        </>
      );
    }
    if (hasQuestions()) {
      return renderItems();
    }
    return 'No questions';
  })();

  return (
    <div className="trigger-side-pane-questions">
      <Card
        header="Unanswered questions"
        body={
          <div className="trigger-side-pane-questions__content">
            <div className="trigger-side-pane-questions__list">
              {renderedQuestions}
            </div>
            <TooltipGuard>
              {_ => (
                <div
                  className="trigger-side-pane-questions__view-more"
                  onClick={onViewMore}
                >
                  View more in Bot Analytics
                </div>
              )}
            </TooltipGuard>
          </div>
        }
      />
    </div>
  );
};
